import { PasseriniAPI } from './apiService';
import {
  Presupuesto,
  TipoMoneda,
  IniciarPresupuestoData,
  EditarMontoPresupuestoData,
  CobroFormData,
  Cobro,
  Documento,
  TipoDocumento,
} from '@/types/presupuesto';

export const presupuestoService = {
  /**
   * Obtener presupuesto de un trámite
   */
  getPresupuesto: async (tramiteId: string): Promise<Presupuesto> => {
    const response = await PasseriniAPI.get(`/tramites/${tramiteId}/presupuesto`);
    return response.data;
  },

  /**
   * Iniciar presupuesto en una moneda específica
   */
  iniciarPresupuesto: async (
    tramiteId: string,
    moneda: TipoMoneda,
    data: IniciarPresupuestoData
  ): Promise<Presupuesto> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/iniciar`,
      data
    );
    return response.data;
  },

  /**
   * Registrar un cobro
   */
  registrarCobro: async (
    tramiteId: string,
    moneda: TipoMoneda,
    data: CobroFormData
  ): Promise<Cobro> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/cobros`,
      data
    );
    return response.data;
  },

  /**
   * Registrar cobro total (automático)
   */
  registrarCobroTotal: async (
    tramiteId: string,
    moneda: TipoMoneda
  ): Promise<Cobro> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/cobros/total`
    );
    return response.data;
  },

  /**
   * Actualizar un cobro existente
   */
  actualizarCobro: async (
    tramiteId: string,
    moneda: TipoMoneda,
    cobroId: string,
    data: CobroFormData
  ): Promise<Cobro> => {
    const response = await PasseriniAPI.put(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/cobros/${cobroId}`,
      data
    );
    return response.data;
  },

  /**
   * Eliminar un cobro
   */
  eliminarCobro: async (
    tramiteId: string,
    moneda: TipoMoneda,
    cobroId: string
  ): Promise<void> => {
    await PasseriniAPI.delete(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/cobros/${cobroId}`
    );
  },

  /**
   * Editar monto del presupuesto
   */
  editarMontoPresupuesto: async (
    tramiteId: string,
    moneda: TipoMoneda,
    data: EditarMontoPresupuestoData
  ): Promise<Presupuesto> => {
    const response = await PasseriniAPI.put(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}/monto`,
      data
    );
    return response.data;
  },

  /**
   * Eliminar (resetear) presupuesto
   */
  eliminarPresupuesto: async (
    tramiteId: string,
    moneda: TipoMoneda
  ): Promise<Presupuesto> => {
    const response = await PasseriniAPI.delete(
      `/tramites/${tramiteId}/presupuesto/${moneda.toLowerCase()}`
    );
    return response.data;
  },

  /**
   * Cargar documento (proforma o factura)
   */
  cargarDocumento: async (
    tramiteId: string,
    tipo: TipoDocumento,
    nombreCustom: string,
    file: File
  ): Promise<Documento> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);
    formData.append('nombre_custom', nombreCustom);

    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto/documentos`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Actualizar documento (nombre y/o categoría)
   */
  actualizarDocumento: async (
    tramiteId: string,
    documentoId: string,
    nombreCustom: string,
    tipo: TipoDocumento
  ): Promise<Documento> => {
    const response = await PasseriniAPI.put(
      `/tramites/${tramiteId}/presupuesto/documentos/${documentoId}`,
      {
        nombre_custom: nombreCustom,
        tipo,
      }
    );
    return response.data;
  },

  /**
   * Eliminar documento
   */
  eliminarDocumento: async (
    tramiteId: string,
    documentoId: string
  ): Promise<void> => {
    await PasseriniAPI.delete(
      `/tramites/${tramiteId}/presupuesto/documentos/${documentoId}`
    );
  },

  /**
   * Descargar documento
   */
  descargarDocumento: async (
    tramiteId: string,
    documentoId: string
  ): Promise<Blob> => {
    const response = await PasseriniAPI.get(
      `/tramites/${tramiteId}/presupuesto/documentos/${documentoId}/download`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },
};
